import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { proposeForfeit } from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay, parseMajorInput, parseMinorUnits } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'

/**
 * Voiding a sale. §6.7 `POS-005`; §17 `OPS-105`; `POS-007`. Posts to `POST /orders/void`,
 * `apps/api/src/orders/reversals.service.ts`.
 *
 * **A void and a refund are two operations, and this panel is only the first.**
 * `reversals.service.ts` says it in terms: a void unwinds a sale that was never recognised, a
 * deposit on glasses nobody collected; a refund reverses one that was. They have separate
 * permissions in Appendix B (`sale_void` and `payment_refund`, both sensitive) so a tenant can
 * grant one without the other, and they post different journals. The panel says which is which in
 * a sentence, because the shopkeeper deciding is the person who has to pick, and on a `fulfilled`
 * or `closed` order it points at the returns desk rather than growing a refund path of its own:
 * the refund surface has blockers filed against it and `later_todo.md` 26 holds them.
 *
 * **The three things a void does, said before it is done.** `OPS-105` calls this a cancellation
 * with a refund decision, and the decision is nobody's to take blind:
 *
 * - *The money.* Every payment taken comes back, less whatever the shop keeps. Nothing is deleted:
 *   each original payment gets a compensating row pointing at it, so the drawer's history reads as
 *   taken and then given back, which is what a cash count has to reconcile against.
 * - *The goods.* `reverseOrderLinesInTx` undoes every sale movement the order's lines relieved.
 *   Lines that relieved nothing contribute nothing, and a lens made to this prescription never was
 *   on a shelf, so the panel says that rather than promising stock back on every line.
 * - *The job.* **Nothing in `voidOrder` touches a job**, and the panel has to say so. The lab
 *   carries on, and `jobs.service.ts`'s own collection guard records where that ends: a job left
 *   `ready` against a voided order, which `ORDER_NOT_COLLECTABLE` then refuses at the counter with
 *   the glasses already made. Cancelling the job is a separate act on the job screen.
 *
 * **Every refusal the server can give is pre-checked here, because a refused write stops the whole
 * queue** until somebody discards it (`OFF-001a`, `OFF-005`). One void refused on a till halts the
 * till, so:
 *
 * - `ORDER_ALREADY_VOID` and `ORDER_ALREADY_FULFILLED`: the status branches below, and they mirror
 *   the server's denylist exactly rather than inventing a stricter one. `cancelled`, `fulfilled`
 *   and `closed` are refused; a draft, a `confirmed`, an `expired` and a status this build has
 *   never heard of are all offered, per `ARC-020`.
 * - `FORFEIT_AMBIGUOUS`: the retained fee and the policy are one radio group, so both can never be
 *   sent together.
 * - `FORFEIT_POLICY_UNSET` and `FORFEIT_POLICY_INVALID`: `proposeForfeit` is the same pure function
 *   the service calls, so the configured percentage is validated here with it rather than
 *   re-derived. A shop with no policy is not offered the policy option at all.
 * - `CASH_SESSION_REQUIRED`: cash handed back comes out of a real drawer (`BR-08`), and the server
 *   requires a session when any live payment was cash. `TakePayment` pre-checks the same condition
 *   in the same words for the same reason, and this panel follows it.
 * - `REASON_REQUIRED`: three characters after trimming, which is the server's own test.
 *
 * **One refusal cannot be pre-checked, and it is named rather than guessed at.**
 * `RETAINED_FEE_EXCEEDS_DEPOSIT` compares the fee against `paymentsTakenMinor`, and no read sends
 * that figure: `order-detail.service.ts` deliberately hands back the payment rows rather than a net
 * paid total, "because re-deriving that logic here risks the two endpoints disagreeing". Rebuilding
 * `paymentsOfOrder`'s four clauses in the counter is the exact duplication `outstanding.ts` names
 * as the source of three separate money defects, so this panel does not do it. The fee is sent and
 * the server's refusal is the answer. The fix belongs on the read: `paid` beside `outstanding` and
 * `refunded`.
 *
 * **There is no `expectedVersion` on this route.** Every other order mutation takes one and
 * `order-detail.service.ts` says so, but `VoidOrder` has no such field and nothing in `voidOrder`
 * compares a version: it takes `select ... for update` on the order row and refuses a second void
 * on the status instead. Sending one anyway would be accepted and ignored, which is worse than
 * sending none, because the screen would look locked while two tills could still race, the loser
 * being refused and stopping the queue. Left out, and recorded as a gap in the route.
 *
 * **The amount shown is only ever the server's.** The panel can say what the policy percentage is,
 * because it holds the tenant setting, but the money it kept and gave back is read off the write's
 * own answer. `voidOrder` commits and then reports `returned` and `retained`; there is no propose
 * step to read first, so nothing here prints a figure before the void has happened.
 */

/** A payment row as the order detail read gives it. Only what the cash pre-check needs. */
export interface VoidSalePayment {
  /** Appendix E `tender`, unnarrowed per `ARC-020`. */
  readonly method: string
  /** Minor units, signed. Negative is a reversal or a refund. */
  readonly amountMinor: string
}

export interface VoidSaleProps {
  readonly orderId: string
  /** What the shop calls the sale, for the queue's own sentence. */
  /**
   * What the shop calls the sale, or null until `TAX-002` numbers it.
   *
   * Nullable rather than taking a label, because the label goes into a sentence here and
   * "A void on Not invoiced yet" is not one. The panel picks the sentence that fits.
   */
  readonly orderNumber: string | null
  /** Appendix C.1's state, a `string` per `ARC-020`. */
  readonly status: string
  /** ISO 4217, the order's own. Every amount on the panel is in it. */
  readonly currency: string
  /** `BR-01`: the order's own branch, which the server checks against the caller's. */
  readonly branchId: string
  /** `BR-08`: the compensating rows are payments too, and they carry the same attribution. */
  readonly terminalId: string
  /** `BR-08`. Absent means no open drawer on this terminal, which cash coming back needs. */
  readonly cashSessionId?: string
  /** The order's payment rows, for the drawer pre-check. */
  readonly payments: readonly VoidSalePayment[]
  /**
   * `POS-007`'s configured forfeit share, from the tenant settings the session context carries.
   *
   * **Null means the shop has set no policy, which is not zero.** `proposeForfeit` refuses rather
   * than defaulting, for the reason it gives: a forfeit invented by the software is the shop
   * keeping a customer's money on nobody's authority.
   */
  readonly forfeitPercent: string | null
  /** Called once the server has taken the void, so the order can be read again. */
  readonly onVoided?: () => void
}

/** What `POST /orders/void` answers. Read defensively: it comes through the queue as `unknown`. */
interface VoidedAnswer {
  readonly returned?: { readonly minor: string; readonly currency: string }
  readonly retained?: { readonly minor: string; readonly currency: string }
}

const answerOf = (response: unknown): VoidedAnswer =>
  typeof response === 'object' && response !== null ? (response as VoidedAnswer) : {}

/** What the shop may do with the deposit. One choice, so `FORFEIT_AMBIGUOUS` cannot be sent. */
type FeeChoice = 'nothing' | 'amount' | 'policy'

/**
 * Whether the configured percentage is one `proposeForfeit` will accept.
 *
 * The domain function rather than a range check written here, so the counter and the service agree
 * by construction about what a usable policy is. A percentage outside 0 to 100, or one somebody
 * typed as words, is `FORFEIT_POLICY_INVALID` on the server: a refusal, and a refusal stops the
 * queue. `paidMinor: 0n` because only the percentage is under test and zero is a valid deposit.
 */
function policyIsUsable(percent: string | null): boolean {
  if (percent === null) return false
  try {
    proposeForfeit({ paidMinor: 0n, policyPercent: percent })
    return true
  } catch {
    return false
  }
}

export function VoidSale({
  orderId,
  orderNumber,
  status,
  currency,
  branchId,
  terminalId,
  cashSessionId,
  payments,
  forfeitPercent,
  onVoided,
}: VoidSaleProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [reason, setReason] = useState('')
  const [choice, setChoice] = useState<FeeChoice>('nothing')
  const [feeText, setFeeText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [answer, setAnswer] = useState<VoidedAnswer | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    // Kept and rendered rather than discarded. What came back and what the shop kept is the whole
    // point of `OPS-105`'s refund decision, and the parent re-reads the order on `onVoided`, which
    // turns the status to `cancelled`: the answer is rendered before any status branch below so
    // that re-read cannot wipe the two figures off the screen.
    setAnswer(answerOf(write.response))
    onVoided?.()
  })

  /**
   * **A void somebody discarded has to give the button back.**
   *
   * The button is gone while `writeId` is set, which is what stops a second void, and
   * `useWriteSettled` only ever fires for a write the server **took**. A blocked write that
   * somebody throws away in `BlockedWrites` therefore never settles, so `writeId` stayed set for
   * good. Worse than a missing button: `writeLine` reads a write that is no longer in the snapshot
   * as saved, so the panel said "The void saved." over a void that never happened and offered no
   * way to try again.
   *
   * That lands on the one refusal this panel cannot pre-check,
   * `RETAINED_FEE_EXCEEDS_DEPOSIT`: the fee was too high, somebody discarded the write, and the
   * till is then telling them the sale is void.
   */
  useEffect(() => {
    if (writeId === null || answer !== null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [answer, snapshot, writeId])

  /**
   * `BR-08`, before the queue rather than after it.
   *
   * `resolveCashSession` refuses with `CASH_SESSION_REQUIRED` when any payment being reversed was
   * cash and no session is named, and the counter already knows the methods. Waiting for that round
   * trip costs the till: the write sits `blocked`, possibly a long time on a bad connection, and
   * everything queued behind it waits with it.
   *
   * **It can over-refuse, and that is the safe direction.** The server reverses only live payments,
   * dropping a tender that has already been reversed, and the order detail mapper does not carry
   * `reversesPaymentId`, so a cash payment already reversed still counts here. The cost is a
   * sentence asking for a drawer that is strictly not needed; the other direction is a halted till.
   */
  const needsDrawer = payments.some((payment) => payment.method === 'cash')
  const policyAvailable = policyIsUsable(forfeitPercent)

  const submit = useCallback(async () => {
    if (terminalId === '') {
      // `requireTerminal` refuses an empty one, and `BR-08` says attribution is never inferred.
      setError(t('voidSale.needsTerminal'))
      return
    }
    if (reason.trim().length < 3) {
      // `requireReason`'s own test, to the character. `ROLE-006`'s override report is read by
      // somebody who was not there, and a void nobody can explain is the shape a shift's takings
      // disappear in.
      setError(t('voidSale.reasonRequired'))
      return
    }
    if (needsDrawer && (cashSessionId === undefined || cashSessionId === '')) {
      setError(t('voidSale.needsDrawer'))
      return
    }

    let retainedFeeMinor: string | null = null
    if (choice === 'amount') {
      let fee: bigint
      try {
        fee = parseMajorInput(feeText, currency)
      } catch {
        // `MR-01`: parsed through `money.ts` and nowhere else, so what the shop keeps is exact
        // minor units rather than a float that approximates them. `parseMajorInput` throws
        // `MoneyInputError` with the offending text in it, which is not a sentence for a till, so
        // the catalogue's own sentence is shown instead.
        setError(t('voidSale.feeInvalid'))
        return
      }
      if (fee < 0n) {
        // `parseMajorInput` accepts a leading sign, and the server would compare a negative fee
        // against the deposit and pass it: the loop would then hand back more than came in.
        setError(t('voidSale.feeNegative'))
        return
      }
      retainedFeeMinor = fee.toString()
    }

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/orders/void',
      label: orderNumber === null
          ? t('voidSale.queueLabelNoNumber')
          : t('voidSale.queueLabel', { number: orderNumber }),
      body: {
        orderId,
        branchId,
        terminalId,
        ...(cashSessionId === undefined || cashSessionId === '' ? {} : { cashSessionId }),
        reason: reason.trim(),
        // One or the other, never both, because both together is `FORFEIT_AMBIGUOUS` and a shop
        // that believed it applied its policy would actually have kept a number somebody typed.
        ...(retainedFeeMinor === null ? {} : { retainedFeeMinor }),
        ...(choice === 'policy' ? { applyForfeitPolicy: true } : {}),
        // No `postedOn`: `branchToday` books the void to the day the shop calls today, and this
        // device's clock is not that day. No `expectedVersion` either; see the header.
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    branchId,
    cashSessionId,
    choice,
    currency,
    enqueue,
    feeText,
    needsDrawer,
    orderId,
    orderNumber,
    reason,
    t,
    terminalId,
  ])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.void'))

  // Before every status branch. The parent re-reads on `onVoided` and the order comes back
  // `cancelled`, so a status check first throws away the two figures this panel exists to show.
  if (answer !== null) {
    const retainedMinor =
      answer.retained === undefined ? null : parseMinorUnits(answer.retained.minor)
    return (
      <section className="lm-void-sale" aria-labelledby="void-sale-heading">
        <h3 id="void-sale-heading" className="lm-void-sale__heading">
          {t('voidSale.heading')}
        </h3>
        {/* Each amount goes in as an element, not as text. `DESIGN-TOKENS.md` §6 keeps money off
            the mirroring list, so in Urdu the sentence reads right to left and the figure inside
            it does not: `sentence()` is what allows that without building one sentence from two
            `t()` calls, which Appendix S forbids. */}
        <div className="lm-void-sale__answer">
          <p>{t('voidSale.done')}</p>
          {answer.returned === undefined ? null : (
            <p>
              {sentence(t, 'voidSale.gaveBack', {
                amount: (
                  <Ltr>{formatWireForDisplay(answer.returned.minor, answer.returned.currency)}</Ltr>
                ),
              })}
            </p>
          )}
          {retainedMinor === null || answer.retained === undefined ? null : retainedMinor === 0n ? (
            // Zero is a real answer and a different sentence from a figure. "Kept by the shop:
            // PKR 0.00" reads as a decision somebody took about nothing.
            <p>{t('voidSale.keptNothing')}</p>
          ) : (
            <p>
              {sentence(t, 'voidSale.kept', {
                amount: (
                  <Ltr>{formatWireForDisplay(answer.retained.minor, answer.retained.currency)}</Ltr>
                ),
              })}
            </p>
          )}
        </div>
        <p className="lm-void-sale__effects">{t('voidSale.whatItDoesJobs')}</p>
      </section>
    )
  }

  if (status === 'cancelled') {
    // `ORDER_ALREADY_VOID`. Said rather than rendered as a missing button, because a panel that
    // vanishes teaches nobody why.
    return (
      <section className="lm-void-sale" aria-labelledby="void-sale-heading">
        <h3 id="void-sale-heading" className="lm-void-sale__heading">
          {t('voidSale.heading')}
        </h3>
        <p className="lm-hint">{t('voidSale.alreadyVoid')}</p>
      </section>
    )
  }

  if (status === 'fulfilled' || status === 'closed') {
    // `ORDER_ALREADY_FULFILLED`. The server's own answer is "refund it instead", and the refusal
    // exists because letting a void through here is how recognised revenue disappears without the
    // refund permission ever being checked. The returns desk is the surface that moves this money.
    return (
      <section className="lm-void-sale" aria-labelledby="void-sale-heading">
        <h3 id="void-sale-heading" className="lm-void-sale__heading">
          {t('voidSale.heading')}
        </h3>
        <p className="lm-hint">{t('voidSale.wasCollected')}</p>
        <p className="lm-void-sale__effects">{t('voidSale.notARefund')}</p>
      </section>
    )
  }

  return (
    <section className="lm-void-sale" aria-labelledby="void-sale-heading">
      <h3 id="void-sale-heading" className="lm-void-sale__heading">
        {t('voidSale.heading')}
      </h3>

      {/* What it does to the money, the goods and the job, before the button that does it. */}
      <p className="lm-void-sale__effects">{t('voidSale.whatItDoesMoney')}</p>
      <p className="lm-void-sale__effects">{t('voidSale.whatItDoesGoods')}</p>
      <p className="lm-void-sale__effects">{t('voidSale.whatItDoesJobs')}</p>
      <p className="lm-void-sale__effects">{t('voidSale.notARefund')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('voidSale.reasonLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value)
          }}
        />
      </label>

      <fieldset className="lm-field">
        <legend className="lm-field__label">{t('voidSale.feeLegend')}</legend>
        <label>
          <input
            type="radio"
            name="void-sale-fee"
            value="nothing"
            checked={choice === 'nothing'}
            onChange={() => {
              setChoice('nothing')
              setError(null)
            }}
          />{' '}
          {t('voidSale.keepNothing')}
        </label>
        <label>
          <input
            type="radio"
            name="void-sale-fee"
            value="amount"
            checked={choice === 'amount'}
            onChange={() => {
              setChoice('amount')
              setError(null)
            }}
          />{' '}
          {t('voidSale.keepAmount')}
        </label>
        {/* Offered only when the shop has a policy `proposeForfeit` will accept. Without one the
            service refuses the request, and a refusal stops the queue, so the option is absent
            rather than present and certain to fail. */}
        {policyAvailable ? (
          <label>
            <input
              type="radio"
              name="void-sale-fee"
              value="policy"
              checked={choice === 'policy'}
              onChange={() => {
                setChoice('policy')
                setError(null)
              }}
            />{' '}
            {t('voidSale.keepPolicy', { percent: forfeitPercent ?? '' })}
          </label>
        ) : null}
      </fieldset>

      {policyAvailable ? null : forfeitPercent === null ? (
        <p className="lm-hint">{t('voidSale.noPolicy')}</p>
      ) : (
        // A policy is configured and is not a percentage. Named with the value, because the fix is
        // a settings correction and nobody can make it without knowing what is in there.
        <p className="lm-hint">{t('voidSale.policyUnusable', { percent: forfeitPercent })}</p>
      )}

      {choice === 'amount' ? (
        <label className="lm-field">
          <span className="lm-field__label">{t('voidSale.feeLabel', { currency })}</span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={feeText}
            onChange={(e) => {
              setFeeText(e.currentTarget.value)
            }}
          />
        </label>
      ) : null}

      {needsDrawer && (cashSessionId === undefined || cashSessionId === '') ? (
        <p className="lm-hint">{t('voidSale.needsDrawer')}</p>
      ) : null}

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/* Gone the moment a write is queued, not once an answer arrives. A second tap is a second
          void, which the server refuses with `ORDER_ALREADY_VOID`, and that refusal stops the whole
          queue: the order is still on screen in its old status until the re-read lands, so nothing
          else would have stopped the tap. */}
      {writeId === null ? (
        <button type="button" className="lm-button lm-button--danger" onClick={() => void submit()}>
          {t('voidSale.submit')}
        </button>
      ) : null}
    </section>
  )
}
